/**
 * VisualLinksPanel - Unified Component for Wireframes, Designs & Design System
 * =============================================================================
 *
 * This unified component handles display and editing of all visual page types.
 *
 * Features:
 * - Preview-before-commit: Changes are previewed first, then user can confirm or cancel
 * - Original file is not modified until user confirms
 *
 * Props:
 * - projectId: Project ID
 * - taskType: 'wireframes' | 'designs' | 'design-system'
 * - conversationId: Optional conversation ID
 * - onPageEdited: Optional callback when a page is edited. Called with {pageId, taskType} for parent to refresh preview
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Divider
} from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as PreviewIcon,
  Web as WebIcon,
  Brush as BrushIcon,
  Palette as PaletteIcon,
  OpenInNew as OpenInNewIcon,
  Home as HomeIcon,
  Article as ArticleIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  AutoFixHigh as ImproveIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import DownloadButton from '../common/DownloadButton';
import visualService from '../../services/visualService';
import WireframeUploadDialog from '../wireframes/WireframeUploadDialog';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import useValidation from '../../hooks/useValidation';

// Icon mapping for task types
const TASK_ICONS = {
  'wireframes': WebIcon,
  'designs': BrushIcon,
  'design-system': PaletteIcon
};

const VisualLinksPanel = ({ projectId, taskType = 'wireframes', conversationId, onPageEdited, refreshKey = 0 }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [indexUrl, setIndexUrl] = useState(null);

  // Task configuration
  const taskConfig = visualService.getTaskConfig(taskType);
  const TaskIcon = TASK_ICONS[taskType] || WebIcon;

  // Page-level validation — uses page_id as validated_id
  const initialValidationState = pages.reduce((acc, page) => {
    acc[page.page_id] = false;
    return acc;
  }, {});

  const {
    validatedItems: validatedPages,
    handleValidateItem: handleValidatePageBase,
    getValidatorType,
    isAssignedValidator,
  } = useValidation({
    conversationId,
    projectId,
    taskId: taskType,
    initialState: initialValidationState,
  });

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [editRequest, setEditRequest] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [pageHtmlContent, setPageHtmlContent] = useState('');
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [lhsViewMode, setLhsViewMode] = useState('preview'); // 'code' or 'preview'
  const [editSuccess, setEditSuccess] = useState(false);
  const [editHistory, setEditHistory] = useState([]);

  // Preview-before-commit state
  const [originalContent, setOriginalContent] = useState(''); // Original file content
  const [previewContent, setPreviewContent] = useState(''); // Preview content (not yet saved)
  const [hasPreviewChanges, setHasPreviewChanges] = useState(false); // Has unsaved preview changes
  const [isSaving, setIsSaving] = useState(false); // Saving/confirming state

  // Upload dialog state - isolated feature for uploading .html/.zip files
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadSelectedPageId, setUploadSelectedPageId] = useState(null);

  useEffect(() => {
    if (projectId && taskType) {
      fetchPageUrls();
    }
  }, [projectId, taskType, conversationId, refreshKey]);

  const fetchPageUrls = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await visualService.getPageUrls(projectId, taskType);

      if (response.success && response.data) {
        setPages(response.data.pages || []);
        setIndexUrl(response.data.index_url);
      }
    } catch (err) {
      console.error(`Error fetching ${taskType} page URLs:`, err);
      setError(err.message || err);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening edit dialog
  const handleOpenEditDialog = async (page) => {
    setEditingPage(page);
    setEditRequest('');
    setPageHtmlContent('');
    setOriginalContent('');
    setPreviewContent('');
    setEditSuccess(false);
    setEditHistory([]);
    setHasPreviewChanges(false);
    setEditDialogOpen(true);

    // Load the HTML content
    setIsLoadingHtml(true);
    try {
      const result = await visualService.getPageContent(projectId, taskType, page.page_id);
      if (result.success && result.data?.html_content) {
        setPageHtmlContent(result.data.html_content);
        setOriginalContent(result.data.html_content);
      } else if (result.data?.content) {
        setPageHtmlContent(result.data.content);
        setOriginalContent(result.data.content);
      } else {
        setPageHtmlContent('<!-- No HTML content found -->');
        setOriginalContent('<!-- No HTML content found -->');
      }
    } catch (error) {
      console.error(`Error loading ${taskType} content:`, error);
      setPageHtmlContent('<!-- Error loading content: ' + (error.message || error) + ' -->');
      setOriginalContent('');
    } finally {
      setIsLoadingHtml(false);
    }
  };

  // Handle closing edit dialog
  const handleCloseEditDialog = async () => {
    // If there are unsaved preview changes, just close without saving
    // The original file was never modified
    setEditDialogOpen(false);
    setEditingPage(null);
    setEditRequest('');
    setPageHtmlContent('');
    setOriginalContent('');
    setPreviewContent('');
    setEditSuccess(false);
    setEditHistory([]);
    setHasPreviewChanges(false);
  };

  // Handle canceling preview changes (revert to original)
  const handleCancelPreview = () => {
    setPageHtmlContent(originalContent);
    setPreviewContent('');
    setHasPreviewChanges(false);
    setEditHistory([]);
    setEditSuccess(false);
  };

  // Handle submitting edit (PREVIEW ONLY - doesn't save to disk)
  const handleSubmitEdit = async () => {
    if (!editingPage || !editRequest.trim()) {
      return;
    }

    setIsEditing(true);
    setEditSuccess(false);

    try {
      // Use preview endpoint - doesn't save to disk
      const result = await visualService.previewPageEdit(
        projectId,
        taskType,
        editingPage.page_id,
        editRequest
      );

      if (result.success && result.data) {
        // Add to edit history
        setEditHistory(prev => [...prev, {
          request: editRequest,
          timestamp: new Date().toISOString()
        }]);

        // Update preview content
        const newPreviewContent = result.data.preview_content;
        setPreviewContent(newPreviewContent);
        setPageHtmlContent(newPreviewContent);

        // Mark as having preview changes
        setHasPreviewChanges(true);

        // Clear edit input
        setEditRequest('');
        setEditSuccess(true);
        setLhsViewMode('preview');

        console.log(`${taskType} page preview generated successfully`);
      }
    } catch (error) {
      console.error(`Error previewing ${taskType} page:`, error);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle confirming and saving the preview to disk
  const handleConfirmSave = async () => {
    if (!editingPage || !previewContent) {
      return;
    }

    setIsSaving(true);

    try {
      // Use confirm endpoint - actually saves to disk
      const result = await visualService.confirmPageEdit(
        projectId,
        taskType,
        editingPage.page_id,
        previewContent
      );

      if (result.success) {
        console.log(`${taskType} page saved successfully`);

        // Refresh page list
        await fetchPageUrls();

        // Notify parent component to refresh its preview
        if (onPageEdited) {
          console.log('VisualLinksPanel: Calling onPageEdited callback with page:', editingPage?.page_id);
          onPageEdited({
            pageId: editingPage?.page_id,
            taskType: taskType
          });
        }

        // Close dialog
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error(`Error saving ${taskType} page:`, error);
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== UPLOAD DIALOG HANDLERS ====================
  // These handlers are isolated and only triggered by explicit user action.
  // They do NOT affect existing edit, preview, or save functionality.

  // Handle opening upload dialog for replacing ALL pages
  const handleOpenUploadDialog = () => {
    setUploadSelectedPageId(null);
    setUploadDialogOpen(true);
  };

  // Handle opening upload dialog for replacing a SINGLE page
  const handleOpenUploadDialogForPage = (pageId) => {
    setUploadSelectedPageId(pageId);
    setUploadDialogOpen(true);
  };

  // Handle upload dialog close
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadSelectedPageId(null);
  };

  // Handle upload complete - refresh page list
  const handleUploadComplete = (result) => {
    console.log('Upload complete:', result);
    // Refresh the page list
    fetchPageUrls();
    // Notify parent if callback provided
    if (onPageEdited) {
      // For single page replacement, use the replaced page_id
      // For "replace all", default to 'index' which is the navigation hub
      // Never use 'all' as it's not a valid page
      let pageIdToShow = 'index';
      if (result?.page_id) {
        // Single page replacement - show the replaced page
        pageIdToShow = result.page_id;
      } else if (result?.pages && result.pages.length > 0) {
        // Replace all - check if index exists in the uploaded pages, otherwise use first page
        const hasIndex = result.pages.some(p => p.page_id === 'index');
        pageIdToShow = hasIndex ? 'index' : result.pages[0].page_id;
      }

      onPageEdited({
        pageId: pageIdToShow,
        taskType: taskType,
        action: 'upload'
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading {taskConfig.title.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {taskConfig.emptyMessage} Generate {taskType} to see links here.
      </Alert>
    );
  }

  // Empty state
  if (pages.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 1 }}>
        <TaskIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {taskConfig.emptyMessage}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {taskConfig.emptySubMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <TaskIcon sx={{ mr: 1, color: 'primary.main' }} />
          {taskConfig.title}
        </Typography>
        <Chip
          label={`${pages.length} pages`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* View All Button — hidden: shell.html handles navigation now */}

      {/* Download & Upload Buttons Row */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        {/* Download All Button */}
        <Box sx={{ flex: 1 }}>
          <DownloadButton
            resourceType={taskType}
            projectId={projectId}
            label={taskConfig.downloadLabel}
            variant="outlined"
            color="primary"
            fullWidth
            sx={{ py: 1.5 }}
          />
        </Box>

        {/* Upload & Replace Button — hidden: use conversation to update pages */}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Individual Pages:
      </Typography>

      {/* Pages List */}
      <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
        {pages.map((page) => (
          <ListItem
            key={page.page_id}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              mb: 1,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          >
            <ListItemIcon>
              {page.is_index ? (
                <HomeIcon color="primary" />
              ) : (
                <ArticleIcon color="action" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={page.is_index ? 600 : 400}>
                  {page.title}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {page.page_type} • {(page.file_size / 1024).toFixed(1)} KB
                </Typography>
              }
            />
            {/* Page validation */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (isAssignedValidator) {
                  handleValidatePageBase(page.page_id, page.title);
                }
              }}
              sx={{
                display: 'flex', alignItems: 'center', ml: 1,
                cursor: isAssignedValidator ? 'pointer' : 'default',
                opacity: isAssignedValidator ? 1 : 0.4,
                pointerEvents: isAssignedValidator ? 'auto' : 'none',
              }}
            >
              {validatedPages[page.page_id] ? (
                <ValidatedIcon size={22} memberType={getValidatorType(page.page_id)} />
              ) : (
                <UnvalidatedIcon size={22} />
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Tip Box */}
      <Box sx={{ mt: 2, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
        <Typography variant="caption" color="text.secondary">
          💡 <strong>Tip:</strong> {taskConfig.tipMessage}
        </Typography>
      </Box>

      {/* Edit Dialog - Split View */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImproveIcon color="primary" />
            <Typography variant="h6">
              Improve {editingPage?.title || 'Page'}
            </Typography>
            {editingPage && (
              <Chip
                label={`${editingPage.page_id}.html`}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
            {hasPreviewChanges && (
              <Chip
                label="Preview Mode"
                size="small"
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Grid container sx={{ flexGrow: 1, height: '100%' }}>
            {/* LHS - HTML Code / Preview Toggle */}
            <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e0e0e0' }, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 1.5, bgcolor: hasPreviewChanges ? '#fff3e0' : '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {lhsViewMode === 'code' ? (
                    <CodeIcon fontSize="small" color="action" />
                  ) : (
                    <PreviewIcon fontSize="small" color="primary" />
                  )}
                  <Typography variant="subtitle2" color="text.secondary">
                    {hasPreviewChanges ? 'Preview (Not Saved)' : (lhsViewMode === 'code' ? 'HTML Code' : 'Page Preview')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Show Preview">
                    <IconButton
                      size="small"
                      onClick={() => setLhsViewMode('preview')}
                      sx={{
                        bgcolor: lhsViewMode === 'preview' ? 'primary.main' : 'transparent',
                        color: lhsViewMode === 'preview' ? 'white' : 'text.secondary',
                        '&:hover': {
                          bgcolor: lhsViewMode === 'preview' ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    >
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Show HTML Code">
                    <IconButton
                      size="small"
                      onClick={() => setLhsViewMode('code')}
                      sx={{
                        bgcolor: lhsViewMode === 'code' ? 'primary.main' : 'transparent',
                        color: lhsViewMode === 'code' ? 'white' : 'text.secondary',
                        '&:hover': {
                          bgcolor: lhsViewMode === 'code' ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    >
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Content area */}
              <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                minHeight: '400px',
                maxHeight: '500px',
                bgcolor: lhsViewMode === 'code' ? '#1e1e1e' : '#fff'
              }}>
                {isLoadingHtml ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1 }}>Loading...</Typography>
                  </Box>
                ) : lhsViewMode === 'code' ? (
                  <Box sx={{ p: 2 }}>
                    <pre style={{
                      margin: 0,
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '12px',
                      lineHeight: '1.5',
                      color: '#d4d4d4',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {pageHtmlContent || '<!-- No content loaded -->'}
                    </pre>
                  </Box>
                ) : (
                  <iframe
                    srcDoc={pageHtmlContent.replace(
                      '</head>',
                      `<style>
                        /* Disable clicks on all interactive elements - preview only */
                        a, button, input, select, textarea, [onclick], [role="button"], nav a, .nav-link, .btn {
                          pointer-events: none !important;
                          cursor: default !important;
                        }
                      </style></head>`
                    )}
                    title="Page Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      minHeight: '400px'
                    }}
                    sandbox="allow-scripts allow-same-origin"
                  />
                )}
              </Box>
            </Grid>

            {/* RHS - Edit Request */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImproveIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Describe Your Changes
                  </Typography>
                </Box>
                {editHistory.length > 0 && (
                  <Chip
                    icon={<HistoryIcon />}
                    label={`${editHistory.length} change${editHistory.length > 1 ? 's' : ''} previewed`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                {/* Preview Mode Alert */}
                {hasPreviewChanges && (
                  <Alert
                    severity="warning"
                    icon={<PreviewIcon />}
                    sx={{ mb: 2 }}
                  >
                    <strong>Preview Mode:</strong> Changes are shown but NOT saved yet. Click "Save Changes" to save or "Cancel" to discard.
                  </Alert>
                )}

                {/* Success Message */}
                {editSuccess && (
                  <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 2 }}
                    onClose={() => setEditSuccess(false)}
                  >
                    Preview generated! Review the changes on the left, then Save or Cancel.
                  </Alert>
                )}

                {/* Edit History */}
                {editHistory.length > 0 && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, maxHeight: '120px', overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      Previewed Changes (Not Saved):
                    </Typography>
                    {editHistory.map((edit, index) => (
                      <Typography key={index} variant="caption" sx={{ display: 'block', color: 'text.secondary', pl: 1, borderLeft: '2px solid #ff9800', mb: 0.5 }}>
                        {index + 1}. {edit.request.length > 60 ? edit.request.substring(0, 60) + '...' : edit.request}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {hasPreviewChanges
                    ? 'Add more changes to preview, or Save/Cancel your changes.'
                    : 'Tell Ailyce what changes you want to make. Changes will be previewed first before saving.'}
                </Typography>
                <TextField
                  autoFocus
                  multiline
                  fullWidth
                  placeholder={`Examples for ${taskConfig.title.toLowerCase()}:
• Add a 'Forgot Password?' link below the login button
• Change the header background to #333333
• Add a search icon in the navigation bar
• Make the submit button larger and change text to 'Get Started'
• Add a new section with testimonials below the hero`}
                  value={editRequest}
                  onChange={(e) => setEditRequest(e.target.value)}
                  disabled={isEditing || isSaving}
                  sx={{
                    flexGrow: 1,
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start'
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important'
                    }
                  }}
                  InputProps={{
                    sx: { minHeight: editHistory.length > 0 ? '150px' : '250px' }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {hasPreviewChanges && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PreviewIcon fontSize="small" />
                {editHistory.length} change{editHistory.length > 1 ? 's' : ''} previewed (not saved)
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Cancel Preview Button - Only show when there are preview changes */}
            {hasPreviewChanges && (
              <Button
                onClick={handleCancelPreview}
                disabled={isEditing || isSaving}
                startIcon={<UndoIcon />}
                sx={{
                  bgcolor: '#e53935',
                  color: 'white',
                  border: '2px solid #e53935',
                  '&:hover': {
                    bgcolor: 'white',
                    color: '#e53935',
                    border: '2px solid #e53935',
                  },
                  '&:disabled': {
                    bgcolor: '#A0AEC0',
                    color: 'white',
                    border: '2px solid #A0AEC0',
                  }
                }}
              >
                Cancel Changes
              </Button>
            )}

            {/* Close/Cancel Button */}
            <Button
              onClick={handleCloseEditDialog}
              disabled={isEditing || isSaving}
              sx={{
                bgcolor: '#4A5568',
                color: 'white',
                border: '2px solid #4A5568',
                '&:hover': {
                  bgcolor: 'white',
                  color: '#4A5568',
                  border: '2px solid #4A5568',
                },
                '&:disabled': {
                  bgcolor: '#A0AEC0',
                  color: 'white',
                  border: '2px solid #A0AEC0',
                }
              }}
            >
              {hasPreviewChanges ? 'Cancel' : 'Cancel'}
            </Button>

            {/* Save Changes Button - Only show when there are preview changes */}
            {hasPreviewChanges && (
              <Button
                onClick={handleConfirmSave}
                disabled={isEditing || isSaving}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{
                  bgcolor: '#38a169',
                  color: 'white',
                  border: '2px solid #38a169',
                  '&:hover': {
                    bgcolor: 'white',
                    color: '#38a169',
                    border: '2px solid #38a169',
                  },
                  '&:disabled': {
                    bgcolor: '#A0AEC0',
                    color: 'white',
                    border: '2px solid #A0AEC0',
                  }
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}

            {/* Apply/Preview Button */}
            <Button
              onClick={handleSubmitEdit}
              disabled={isEditing || isSaving || !editRequest.trim()}
              startIcon={isEditing ? <CircularProgress size={16} color="inherit" /> : <PreviewIcon />}
              sx={{
                bgcolor: '#4A5568',
                color: 'white',
                border: '2px solid #4A5568',
                '&:hover': {
                  bgcolor: 'white',
                  color: '#4A5568',
                  border: '2px solid #4A5568',
                },
                '&:disabled': {
                  bgcolor: '#A0AEC0',
                  color: 'white',
                  border: '2px solid #A0AEC0',
                }
              }}
            >
              {isEditing ? 'Generating Preview...' : 'Preview Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Upload & Replace Dialog - Isolated feature for uploading .html/.zip files */}
      {/* This dialog does NOT affect existing edit, preview, or save functionality */}
      <WireframeUploadDialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        projectId={projectId}
        taskType={taskType}
        pages={pages}
        selectedPageId={uploadSelectedPageId}
        onUploadComplete={handleUploadComplete}
      />
    </Paper>
  );
};

export default VisualLinksPanel;
